import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { trailPageUrls } from "./buildTrail";
import { stripTrailingSlash } from "./routeUrl";

const BreadcrumbContributionsContext = createContext({
  labels: {},
  parents: {},
  tab: null,
  queries: {},
});
const BreadcrumbActionsContext = createContext(null);

// Sets `value` under `key`, and removes it only if it is still the value this caller
// set: on a navigation the next page may write its own before the previous page's
// cleanup runs.
function setEntry(setMap, key, value) {
  setMap((current) =>
    current[key] === value ? current : { ...current, [key]: value }
  );
}

function clearEntry(setMap, key, value) {
  setMap((current) => {
    if (current[key] !== value) return current;
    const { [key]: _removed, ...rest } = current;
    return rest;
  });
}

// Two contexts so a page registering a name does not re-render itself: only the
// bar and the Back buttons read the contributions, pages call the actions.
export default function BreadcrumbProvider({ children }) {
  const [labels, setLabels] = useState({});
  const [parents, setParents] = useState({});
  const [tab, setTab] = useState(null);
  // The query each level of the current branch was last shown with, for Back.
  const [queries, setQueries] = useState({});
  const { pathname, search } = useLocation();
  const currentPath = stripTrailingSlash(pathname);
  const shownTrail = useRef([]);

  // Leaving a page forgets every level outside its trail, so a list's filters come
  // back only to a page reached from that list. Snapshots the trail of the page being
  // left before the effect below replaces it: the updater may run at the next render.
  useEffect(() => {
    const leftTrail = shownTrail.current;
    setQueries((current) => {
      const kept = { [currentPath]: search };
      for (const url of leftTrail) {
        if (url !== currentPath && url in current) kept[url] = current[url];
      }
      return kept;
    });
  }, [currentPath, search]);

  // Kept current as late parents register (`useBreadcrumbParent`).
  useEffect(() => {
    shownTrail.current = trailPageUrls(currentPath, parents);
  }, [currentPath, parents]);

  const setLabel = useCallback(
    (url, label) => setEntry(setLabels, url, label),
    []
  );
  const clearLabel = useCallback(
    (url, label) => clearEntry(setLabels, url, label),
    []
  );
  const setParent = useCallback(
    (url, parent) => setEntry(setParents, url, parent),
    []
  );
  const clearParent = useCallback(
    (url, parent) => clearEntry(setParents, url, parent),
    []
  );
  const clearTab = useCallback((value) => {
    setTab((current) => (current === value ? null : current));
  }, []);

  const actions = useMemo(
    () => ({
      setLabel,
      clearLabel,
      setParent,
      clearParent,
      setTab,
      clearTab,
    }),
    [setLabel, clearLabel, setParent, clearParent, clearTab]
  );
  const contributions = useMemo(
    () => ({ labels, parents, tab, queries }),
    [labels, parents, tab, queries]
  );

  return (
    <BreadcrumbActionsContext.Provider value={actions}>
      <BreadcrumbContributionsContext.Provider value={contributions}>
        {children}
      </BreadcrumbContributionsContext.Provider>
    </BreadcrumbActionsContext.Provider>
  );
}

export function useBreadcrumbContributions() {
  return useContext(BreadcrumbContributionsContext);
}

export function useBreadcrumbActions() {
  return useContext(BreadcrumbActionsContext);
}
